/**
 * The words for the server's pickup time.
 *
 * The time itself is computed ONLY by lagniappe-server (`calculatePickupDateTime`); the theme does no
 * pickup arithmetic of its own. scanner-dev "Pickup Ready Time Promise.md".
 */

/**
 * Today's date in Central time as "YYYY-MM-DD" — the server's calendar, never the browser's.
 *
 * ⚠ Built from `formatToParts`, never from a locale's whole-string format: an ICU update changed
 * `en-CA`'s date format once, and a key in the wrong shape made `addDaysToKey` throw inside the
 * transit render.
 */
function todayCentralKey(now: Date): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Chicago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const get = (type: string): string => parts.find((part) => part.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** "YYYY-MM-DD" plus `days`, by calendar arithmetic (no time zone involved). */
function addDaysToKey(key: string, days: number): string | null {
  const [year, month, day] = key.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day + days));
  // Never throw from a render helper: a malformed key is "no tomorrow", not a RangeError.
  return isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
}

/**
 * Render the server's pickup time as "today by 2:45 PM", "tomorrow by 9:30 AM" or
 * "Mon, Oct 5 by 9:30 AM".
 *
 * `pickupDateTime` is a zone-less Central wall-clock string ("2026-10-02T09:30:00"), so it is read
 * as DIGITS and compared against Central's today: a shopper in another time zone sees the same words
 * the cart shows, and never an hour shifted by `new Date()` parsing.
 *
 * @param pickupDateTime - "YYYY-MM-DDTHH:MM[:SS]"
 * @param now - The current instant (injectable for tests)
 * @returns The phrase, or null when the string is not a datetime
 */
export function formatPickupReady(pickupDateTime: string, now: Date = new Date()): string | null {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/.exec(pickupDateTime);
  if (!match) return null;
  const [, dateKey, hourText, minuteText] = match;
  const hours = Number(hourText);
  const time = `${hours % 12 || 12}:${minuteText} ${hours >= 12 ? "PM" : "AM"}`;

  const today = todayCentralKey(now);
  let day: string;
  if (dateKey === today) {
    day = "today";
  } else if (dateKey === addDaysToKey(today, 1)) {
    day = "tomorrow";
  } else {
    const [year, month, date] = dateKey.split("-").map(Number);
    day = new Date(Date.UTC(year, month - 1, date)).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
  }
  return `${day} by ${time}`;
}
